import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { api } from "../lib/api";
import { toast } from "sonner";
import AppHeader from "../components/AppHeader";
import DisclaimerBanner from "../components/DisclaimerBanner";
import SiteFooter from "../components/SiteFooter";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { ShieldCheck, KeyRound, Copy, Check } from "lucide-react";

const inputCls =
  "rounded-none border-slate-300 focus:border-slate-900 focus:ring-1 focus:ring-slate-900";

export default function MfaSetup() {
  const { user, checkAuth } = useAuth();
  const nav = useNavigate();
  const [enroll, setEnroll] = useState(null); // { otpauth_uri, secret, recovery_codes }
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [ackDownloaded, setAckDownloaded] = useState(false);

  const alreadyEnrolled = user?.mfa_status?.enrolled;

  useEffect(() => {
    if (!user) return;
    if (alreadyEnrolled) {
      // Already enrolled, direct them to the verify page if not verified
      if (!user.mfa_status?.verified) nav("/mfa/verify");
    }
  }, [user, alreadyEnrolled, nav]);

  const begin = async () => {
    setBusy(true);
    try {
      const r = await api.post("/mfa/enroll");
      setEnroll(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start enrollment");
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    if (code.trim().length !== 6) {
      toast.error("Enter the 6-digit code from your authenticator app");
      return;
    }
    setBusy(true);
    try {
      await api.post("/mfa/enroll/confirm", { code: code.trim() });
      toast.success("Two-factor authentication enabled");
      await checkAuth();
      nav("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verification failed");
    } finally {
      setBusy(false);
    }
  };

  const copyCode = (c, idx) => {
    navigator.clipboard.writeText(c);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 1200);
  };

  const downloadCodes = () => {
    if (!enroll) return;
    const body =
      `ProSe.Claims — MFA Recovery Codes\nGenerated ${new Date().toISOString()}\n\n` +
      enroll.recovery_codes.map((c, i) => `${String(i + 1).padStart(2, "0")}. ${c}`).join("\n") +
      "\n\nEach code works once. Store them somewhere safe.\n";
    const blob = new Blob([body], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "prose-claims-recovery-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
    setAckDownloaded(true);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <DisclaimerBanner />
      <AppHeader />
      <main className="max-w-[820px] mx-auto px-6 py-12 md:py-16 flex-1 w-full" data-testid="mfa-setup-page">
        <div className="label-tiny mb-3">Security</div>
        <h1 className="font-serif-display text-4xl md:text-5xl leading-tight text-slate-900">
          <ShieldCheck className="inline-block w-9 h-9 mr-2 -mt-2 text-slate-700" strokeWidth={1.75} />
          Set up two-factor authentication
        </h1>
        <p className="mt-4 text-slate-600 max-w-2xl">
          Administrators and approved attorneys must protect their account with a
          time-based one-time passcode (TOTP). Use an authenticator app like Google
          Authenticator, 1Password, Authy, or Bitwarden.
        </p>

        {!enroll ? (
          <div className="mt-10 border border-slate-200 p-8">
            <p className="text-slate-700 mb-6">
              We&apos;ll generate a QR code you scan with your authenticator app, plus 10 single-use recovery codes.
            </p>
            <Button
              onClick={begin}
              disabled={busy}
              className="rounded-none bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="mfa-begin-enroll-btn"
            >
              {busy ? "Preparing…" : "Begin setup"}
            </Button>
          </div>
        ) : (
          <div className="mt-10 space-y-8">
            {/* QR */}
            <div className="border border-slate-200 p-6 md:p-8" data-testid="mfa-qr-block">
              <h2 className="font-serif-display text-2xl text-slate-900 mb-3">
                <span className="font-mono-tech text-sm text-slate-500 mr-3">01</span>
                Scan the QR code
              </h2>
              <div className="grid md:grid-cols-[220px_1fr] gap-6 items-start">
                <div className="border border-slate-200 p-3 bg-white inline-block">
                  <QRCodeSVG value={enroll.otpauth_uri} size={192} level="M" includeMargin={false} />
                </div>
                <div className="text-sm text-slate-700 leading-relaxed">
                  <p>Open your authenticator app and scan the code, or enter this secret manually:</p>
                  <div className="mt-3 border border-slate-200 p-3 bg-slate-50 font-mono-tech text-xs break-all" data-testid="mfa-secret">
                    {enroll.secret}
                  </div>
                  <p className="mt-3 text-xs text-slate-500">
                    Enrollment expires in 15 minutes. If you don&apos;t finish, start again.
                  </p>
                </div>
              </div>
            </div>

            {/* Recovery codes */}
            <div className="border border-slate-200 p-6 md:p-8" data-testid="mfa-recovery-block">
              <h2 className="font-serif-display text-2xl text-slate-900 mb-3">
                <span className="font-mono-tech text-sm text-slate-500 mr-3">02</span>
                <KeyRound className="inline-block w-6 h-6 mr-1 -mt-1 text-amber-600" strokeWidth={1.75} />
                Save your recovery codes
              </h2>
              <p className="text-sm text-slate-700 mb-4 max-w-2xl">
                Each code works once. If you lose your authenticator, a recovery code is the only way back in.
                We show these now and never again.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
                {enroll.recovery_codes.map((c, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => copyCode(c, i)}
                    className="border border-slate-200 bg-slate-50 hover:bg-slate-100 px-3 py-2 font-mono-tech text-xs text-slate-800 flex items-center justify-between gap-2 group"
                    data-testid={`recovery-code-${i}`}
                    title="Click to copy"
                  >
                    <span>{c}</span>
                    {copiedIdx === i ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3 opacity-40 group-hover:opacity-100" />}
                  </button>
                ))}
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={downloadCodes}
                className="rounded-none border-slate-300"
                data-testid="download-recovery-codes-btn"
              >
                Download .txt
              </Button>
              {ackDownloaded && (
                <span className="ml-3 text-xs text-emerald-700">Downloaded. Keep the file secure.</span>
              )}
            </div>

            {/* Verify */}
            <div className="border border-slate-200 p-6 md:p-8" data-testid="mfa-confirm-block">
              <h2 className="font-serif-display text-2xl text-slate-900 mb-3">
                <span className="font-mono-tech text-sm text-slate-500 mr-3">03</span>
                Enter the 6-digit code from your app
              </h2>
              <div className="flex flex-wrap items-end gap-3">
                <div className="w-52">
                  <Label className="label-tiny mb-2 block">Code</Label>
                  <Input
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    inputMode="numeric"
                    maxLength={6}
                    autoComplete="one-time-code"
                    placeholder="123456"
                    className={`${inputCls} font-mono-tech tracking-widest text-lg`}
                    data-testid="mfa-code-input"
                    onKeyDown={(e) => { if (e.key === "Enter") confirm(); }}
                  />
                </div>
                <Button
                  onClick={confirm}
                  disabled={busy || code.length !== 6}
                  className="rounded-none bg-slate-900 hover:bg-slate-800 text-white"
                  data-testid="mfa-confirm-btn"
                >
                  {busy ? "Verifying…" : "Enable two-factor"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
